import { LoadState } from "../../../constants/enums";
import { getUniqueId } from "../../../helpers";
import {
  IRiskProfileRadiusOfOperationRow,
  IRiskProfileFleetHistoryRow,
  IRiskProfileYardAddressSecurityRow,
  IRiskProfileCargoCglCoverageRow,
  IRiskProfileVehicleCoverageScheduleRow,
  IRiskProfileDriverHiring,
  IRiskProfileDriverTraining,
  IRiskProfileDriverMonitoring,
  IRiskProfileDriverDiscipline,
  IRiskProfileVehicleManagement,
  IRiskProfileCargoSafety,
  IRiskProfileSecurityMeasures,
  IRiskProfileNonOwnedTrailers,
  IRiskProfileCommodityRow,
  IRiskProfileTerritory,
  IRiskProfileApplicantOperations,
  IRiskProfileCargoOperations,
  IRiskProfilePremises,
  IRiskProfileDriver,
  IRiskProfileVehicle,
  CARGO_CGL_DEFAULT_ROWS,
} from "./commAutoRiskProfile.types";
import { IIftaFilingItem } from "./iftaImport.types";

// ===== V2 JSON column interfaces =====

/** broker_details JSON column */
export interface IRiskProfileV2BrokerDetails {
  broker_name: string | null;
  broker_code: string | null;
  producer: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  marketer: string | null;
}

/** insured_details JSON column */
export interface IRiskProfileV2InsuredDetails {
  named_insured: string | null;
  form_of_business: string | null;
  mailing_address: string | null;
  line_of_business: string | null;
  contact: string | null;
  email: string | null;
  phone_ext_mobile: string | null;
  position: string | null;
}

/** policy_details JSON column */
export interface IRiskProfileV2PolicyDetails {
  effective_date: string | null;
  expiry_date: string | null;
  line_of_business: string | null;
  form_of_business: string | null;
}

/** ownership_details JSON column */
export interface IRiskProfileV2OwnershipRow {
  key: string;
  name: string | null;
  title: string | null;
  years_of_experience: number | null;
}

export interface IRiskProfileV2OwnershipDetails {
  owners: IRiskProfileV2OwnershipRow[];
}

/** applicant_operation_details JSON column */
export interface IRiskProfileV2ApplicantOperationDetails {
  applicant_operations: IRiskProfileApplicantOperations;
}

/** risk_details JSON column */
export interface IRiskProfileV2RiskDetails {
  rin_number: string | null;
  cvor_number: string | null;
  mc_number: string | null;
  dot_number: string | null;
  cvor_status: string | null;
  cvor_violation_rate: number | null;
  vehicle_out_of_service_pct: number | null;
  driver_out_of_service_pct: number | null;
  brief_history_of_risk: string | null;
  description_of_operations: string | null;
  drivers_take_vehicle_home: "Y" | "N" | "Not Applicable" | null;
  loaded_trailers_unattended: "Y" | "N" | "Not Applicable" | null;
  number_of_irp_plates: number | null;
  number_of_prp_plates: number | null;
  number_of_ppv: number | null;
  number_of_unplated_shunt_trucks: number | null;
  all_vehicles_domiciled_in_ontario: "Y" | "N" | "Not Applicable" | null;
  locations_showing_but_not_on_ifta: "Y" | "N" | "Not Applicable" | null;
  radius_of_operation: IRiskProfileRadiusOfOperationRow[];
  fleet_history: IRiskProfileFleetHistoryRow[];
}

/** yard_address_and_security JSON column */
export interface IRiskProfileV2YardAddressAndSecurity {
  yard_address_security: IRiskProfileYardAddressSecurityRow[];
}

/** security_measures JSON column */
export interface IRiskProfileV2SecurityMeasures {
  security_measures: IRiskProfileSecurityMeasures;
}

/** fleet_history_or_previous_policies JSON column */
export interface IRiskProfileV2FleetHistoryOrPreviousPolicies {
  fleet_history: IRiskProfileFleetHistoryRow[];
}

/** vehicle_details JSON column */
export interface IRiskProfileV2VehicleDetails {
  vehicles: IRiskProfileVehicle[];
}

/** non_owned_trailers_details JSON column */
export interface IRiskProfileV2NonOwnedTrailersDetails {
  non_owned_trailers: IRiskProfileNonOwnedTrailers;
}

/** radius_of_operation_details JSON column */
export interface IRiskProfileV2RadiusOfOperationDetails {
  radius_of_operation: IRiskProfileRadiusOfOperationRow[];
}

/** territory_covered_details JSON column */
export interface IRiskProfileV2TerritoryCoveredDetails {
  territory: IRiskProfileTerritory;
}

/** commodities_details JSON column */
export interface IRiskProfileV2CommoditiesDetails {
  commodities_transported: IRiskProfileCommodityRow[];
}

/** travel_details JSON column */
export interface IRiskProfileV2TravelStateRow {
  key: string;
  state_province: string;
  pct_km: number | null;
  auto_no: string | null;
  // --- IFTA extensions (optional) ---
  code?: string | null;
  name?: string | null;
  total_km?: number | null;
  pct_of_country?: number | null;
  pct_of_total?: number | null;
  quarterly_km?: Record<string, number> | null;
  source?: "IFTA_IMPORT" | "MANUAL" | null;
}

export interface IRiskProfileV2TravelDetails {
  usa_states: IRiskProfileV2TravelStateRow[];
  canada_provinces: IRiskProfileV2TravelStateRow[];
  list_required_filings: string | null;
  list_required_filings_v2?: IIftaFilingItem[] | null;
}

/** coverage_details JSON column */
export interface IRiskProfileV2CoverageDetails {
  vehicle_coverage_schedule: IRiskProfileVehicleCoverageScheduleRow[];
  cargo_cgl_coverage: IRiskProfileCargoCglCoverageRow[];
}

/** cargo_and_cgl_details JSON column */
export interface IRiskProfileV2CargoAndCglDetails {
  cargo_operations: IRiskProfileCargoOperations;
}

/** driver_details JSON column */
export interface IRiskProfileV2DriverDetails {
  drivers: IRiskProfileDriver[];
}

/** previous_claim_details JSON column */
export interface IRiskProfileV2PreviousClaimRow {
  key: string;
  driver_no: string | null;
  auto_no: string | null;
  date_ymd: string | null;
  coverage_claim_paid_under_bi: string | null;
  coverage_claim_paid_under_pd: string | null;
  coverage_claim_paid_under_ab: string | null;
  coverage_claim_paid_under_dcpd: string | null;
  coverage_claim_paid_under_ua: string | null;
  coverage_claim_paid_under_collision: string | null;
  coverage_claim_paid_under_comp: string | null;
  amount_paid_or_estimate: number | null;
  location_province_city: string | null;
  details: string | null;
}

export interface IRiskProfileV2PreviousClaimDetails {
  claims: IRiskProfileV2PreviousClaimRow[];
}

/** cargo_claim_details JSON column */
export interface IRiskProfileV2CargoClaimRow {
  key: string;
  date_of_loss: string | null;
  type_of_claim: string | null;
  deductible: number | null;
  payment: number | null;
  expenses: number | null;
  reserve: number | null;
}

export interface IRiskProfileV2CargoClaimDetails {
  cargo_claims: IRiskProfileV2CargoClaimRow[];
}

/** conviction_details JSON column */
export interface IRiskProfileV2ConvictionRow {
  key: string;
  driver_no: string | null;
  driver_name: string | null;
  date_of_conviction: string | null;
  conviction_code: string | null;
  description: string | null;
  conviction_as_result_of_accident: string | null;
  number_of_kms_over: number | null;
  suspended: string | null;
  suspension_reason: string | null;
}

export interface IRiskProfileV2ConvictionDetails {
  convictions: IRiskProfileV2ConvictionRow[];
}

/** driver_hiring_questionnaire JSON column */
export interface IRiskProfileV2DriverHiringQuestionnaire {
  driver_hiring: IRiskProfileDriverHiring;
}

/** driver_training_questionnaire JSON column */
export interface IRiskProfileV2DriverTrainingQuestionnaire {
  driver_training: IRiskProfileDriverTraining;
}

/** driver_monitoring_questionnaire JSON column */
export interface IRiskProfileV2DriverMonitoringQuestionnaire {
  driver_monitoring: IRiskProfileDriverMonitoring;
}

/** driver_discipline_questionnaire JSON column */
export interface IRiskProfileV2DriverDisciplineQuestionnaire {
  driver_discipline: IRiskProfileDriverDiscipline;
}

/** vehicle_management_questionnaire JSON column */
export interface IRiskProfileV2VehicleManagementQuestionnaire {
  vehicle_management: IRiskProfileVehicleManagement;
}

/** cargo_safety_questionnaire JSON column */
export interface IRiskProfileV2CargoSafetyQuestionnaire {
  cargo_safety: IRiskProfileCargoSafety;
}

/** cargo_operations_questionnaire JSON column */
export interface IRiskProfileV2CargoOperationsQuestionnaire {
  cargo_operations: IRiskProfileCargoOperations;
}

/** property_details JSON column */
export interface IRiskProfileV2PropertyDetails {
  premises: IRiskProfilePremises;
}

/** document_details JSON column */
export interface IRiskProfileV2DocumentDetails {
  documents?: any[];
}

/** declaration_details JSON column */
export interface IRiskProfileV2DeclarationDetails {
  declaration_text: string | null;
  applicant_signature: string | null;
  applicant_date: string | null;
  applicant_name_title: string | null;
  broker_signature: string | null;
  broker_date: string | null;
  broker_name_title: string | null;
}

// ===== Main V2 interface matching DB schema =====

export interface IRiskProfileV2 {
  lead_id?: string | null;
  policy_commauto_risk_profile_v2_id: number | null;
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  broker_details: IRiskProfileV2BrokerDetails | null;
  insured_details: IRiskProfileV2InsuredDetails | null;
  policy_details: IRiskProfileV2PolicyDetails | null;
  ownership_details: IRiskProfileV2OwnershipDetails | null;
  applicant_operation_details: IRiskProfileV2ApplicantOperationDetails | null;
  risk_details: IRiskProfileV2RiskDetails | null;
  yard_address_and_security: IRiskProfileV2YardAddressAndSecurity | null;
  security_measures: IRiskProfileV2SecurityMeasures | null;
  fleet_history_or_previous_policies: IRiskProfileV2FleetHistoryOrPreviousPolicies | null;
  remarks: string | null;
  vehicle_details: IRiskProfileV2VehicleDetails | null;
  non_owned_trailers_details: IRiskProfileV2NonOwnedTrailersDetails | null;
  radius_of_operation_details: IRiskProfileV2RadiusOfOperationDetails | null;
  territory_covered_details: IRiskProfileV2TerritoryCoveredDetails | null;
  commodities_details: IRiskProfileV2CommoditiesDetails | null;
  travel_details: IRiskProfileV2TravelDetails | null;
  coverage_details: IRiskProfileV2CoverageDetails | null;
  cargo_and_cgl_details: IRiskProfileV2CargoAndCglDetails | null;
  driver_details: IRiskProfileV2DriverDetails | null;
  previous_claim_details: IRiskProfileV2PreviousClaimDetails | null;
  cargo_claim_details: IRiskProfileV2CargoClaimDetails | null;
  conviction_details: IRiskProfileV2ConvictionDetails | null;
  driver_hiring_questionnaire: IRiskProfileV2DriverHiringQuestionnaire | null;
  driver_training_questionnaire: IRiskProfileV2DriverTrainingQuestionnaire | null;
  driver_monitoring_questionnaire: IRiskProfileV2DriverMonitoringQuestionnaire | null;
  driver_discipline_questionnaire: IRiskProfileV2DriverDisciplineQuestionnaire | null;
  vehicle_management_questionnaire: IRiskProfileV2VehicleManagementQuestionnaire | null;
  cargo_safety_questionnaire: IRiskProfileV2CargoSafetyQuestionnaire | null;
  cargo_operations_questionnaire: IRiskProfileV2CargoOperationsQuestionnaire | null;
  property_details: IRiskProfileV2PropertyDetails | null;
  document_details: IRiskProfileV2DocumentDetails | null;
  declaration_details: IRiskProfileV2DeclarationDetails | null;
  status: "ACTIVE" | "INACTIVE";
  created_by_id: number | null;
  created_by_name: string | null;
  modified_by_id: number | null;
  modified_by_name: string | null;
  create_ts: string | null;
  insert_ts: string | null;
}

export interface IRiskProfileV2State {
  list: {
    data: IRiskProfileV2[];
    loading: LoadState;
    error: string | null;
  };
  form: {
    data: IRiskProfileV2;
    loading: LoadState;
    saveLoading: boolean;
    error: string | null;
  };
}

export const INITIAL_RISK_PROFILE_V2: IRiskProfileV2 = {
  lead_id: null,
  policy_commauto_risk_profile_v2_id: null,
  customer_policy_id: null,
  incremental_number: null,
  customer_id: null,
  broker_details: {
    broker_name: null,
    broker_code: null,
    producer: null,
    address: null,
    phone: null,
    email: null,
    website: null,
    marketer: null,
  },
  insured_details: {
    named_insured: null,
    form_of_business: null,
    mailing_address: null,
    line_of_business: null,
    contact: null,
    email: null,
    phone_ext_mobile: null,
    position: null,
  },
  policy_details: {
    effective_date: null,
    expiry_date: null,
    line_of_business: null,
    form_of_business: null,
  },
  ownership_details: {
    owners: [
      { key: getUniqueId(), name: null, title: null, years_of_experience: null },
    ],
  },
  applicant_operation_details: {
    applicant_operations: {
      haul_owned_product_exclusively: null,
      haul_non_owned_goods_exclusive_agreement: null,
      owners_operated_under_another_name: null,
      back_hauls_guaranteed: null,
      pct_owned_products: null,
      pct_non_owned_goods: null,
      years_operated_under_present_name: null,
      revenue_long_haul_trucking: null,
      companies_under_exclusive_agreement: null,
      previous_business_name: null,
      previous_cvor_rin_dot_mc: null,
      carrier_type: [],
    },
  },
  risk_details: {
    rin_number: null,
    cvor_number: null,
    mc_number: null,
    dot_number: null,
    cvor_status: null,
    cvor_violation_rate: null,
    vehicle_out_of_service_pct: null,
    driver_out_of_service_pct: null,
    brief_history_of_risk: null,
    description_of_operations: null,
    drivers_take_vehicle_home: null,
    loaded_trailers_unattended: null,
    number_of_irp_plates: null,
    number_of_prp_plates: null,
    number_of_ppv: null,
    number_of_unplated_shunt_trucks: null,
    all_vehicles_domiciled_in_ontario: null,
    locations_showing_but_not_on_ifta: null,
    radius_of_operation: [
      {
        key: getUniqueId(),
        vehicles: null,
        radius: null,
        cities_visited: null,
        province: null,
        attached_equipment: null,
        value_of_attachment: null,
        km_0_40_pct: null,
        km_41_80_pct: null,
        km_81_160_pct: null,
        km_161_400_pct: null,
        km_401_800_pct: null,
        km_801_1600_pct: null,
        km_1601_2400_pct: null,
        km_2401_plus_pct: null,
      },
    ],
    fleet_history: [
      {
        key: getUniqueId(),
        term: null,
        insurer: null,
        policy_number: null,
        policy_type: null,
        number_of_vehicles: null,
        number_of_trailers: null,
        claims_payout: null,
        term_premium: null,
        current_status: null,
      },
    ],
  },
  yard_address_and_security: {
    yard_address_security: [
      {
        key: getUniqueId(),
        incremental_number: 1,
        address: null,
        security: [],
      },
    ],
  },
  security_measures: {
    security_measures: {
      vehicles_parked_in_secured_yard: null,
      lot_locked_at_night: null,
      power_units_anti_theft: null,
      loaded_trailers_secured_when_parked: null,
      where_vehicles_parked_off_duty: null,
      number_vehicles_in_secured_yard: null,
      total_value_including_cargo: null,
      lot_has: [],
    },
  },
  fleet_history_or_previous_policies: {
    fleet_history: [
      {
        key: getUniqueId(),
        term: null,
        insurer: null,
        policy_number: null,
        policy_type: null,
        number_of_vehicles: null,
        number_of_trailers: null,
        claims_payout: null,
        term_premium: null,
        current_status: null,
      },
    ],
  },
  remarks: null,
  vehicle_details: {
    vehicles: [],
  },
  non_owned_trailers_details: {
    non_owned_trailers: {
      use_non_owned_trailers: null,
      trailer_type: [],
      number_average: null,
      number_maximum: null,
      value_average: null,
      value_maximum: null,
    },
  },
  radius_of_operation_details: {
    radius_of_operation: [
      {
        key: getUniqueId(),
        vehicles: null,
        radius: null,
        cities_visited: null,
        province: null,
        attached_equipment: null,
        value_of_attachment: null,
        km_0_40_pct: null,
        km_41_80_pct: null,
        km_81_160_pct: null,
        km_161_400_pct: null,
        km_401_800_pct: null,
        km_801_1600_pct: null,
        km_1601_2400_pct: null,
        km_2401_plus_pct: null,
      },
    ],
  },
  territory_covered_details: {
    territory: {
      total_annual_mileage_km: null,
      canada_pct: null,
      usa_pct: null,
      usa_travel_over_100km_from_border: null,
      more_than_5_trips_per_month_usa: null,
      telematics_installed: null,
      telematics_count: null,
      fuel_tax_ifta_attached: null,
    },
  },
  commodities_details: {
    commodities_transported: [
      {
        key: getUniqueId(),
        commodity: null,
        pct_of_loads: null,
        max_value_per_load: null,
        annual_revenue_pct: null,
      },
    ],
  },
  travel_details: {
    usa_states: [
      { key: getUniqueId(), state_province: "Alabama", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Alaska", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Arizona", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Arkansas", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "California", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Colorado", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Connecticut", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Delaware", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "D.C", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Florida", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Georgia", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Hawaii", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Idaho", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Illinois", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Indiana", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Iowa", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Kansas", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Kentucky", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Louisiana", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Maine", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Maryland", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Massachusetts", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Michigan", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Minnesota", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Mississippi", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Missouri", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Montana", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Nebraska", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Nevada", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "New Hampshire", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "New Jersey", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "New Mexico", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "New York", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "North Carolina", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "North Dakota", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Ohio", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Oklahoma", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Oregon", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Pennsylvania", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Rhode Island", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "S. Carolina", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "S. Dakota", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Tennessee", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Texas", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Utah", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Vermont", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Virginia", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Washington", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "W. Virginia", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Wisconsin", pct_km: null, auto_no: null },
    ],
    canada_provinces: [
      { key: getUniqueId(), state_province: "Alberta", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "B.C.", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Manitoba", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Nunavut", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "N.B.", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Nfld.", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Nova Scotia", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Ontario", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "P.E.I.", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Quebec", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Saskatchewan", pct_km: null, auto_no: null },
      { key: getUniqueId(), state_province: "Yukon", pct_km: null, auto_no: null },
    ],
    list_required_filings: null,
  },
  coverage_details: {
    vehicle_coverage_schedule: [
      {
        key: getUniqueId(),
        incremental_number: 1,
        customer_policy_id: null,
      },
    ],
    cargo_cgl_coverage: CARGO_CGL_DEFAULT_ROWS,
  },
  cargo_and_cgl_details: {
    cargo_operations: {
      haul_loads_require_escort: null,
      haul_oversize_overweight: null,
      cargo_value_exceeds_500k: null,
      placards_required: null,
      passengers_allowed: null,
      operate_under_uiia: null,
      vehicles_used_off_road_logging: null,
      residential_home_delivery: null,
      loads_brokered_to_others: null,
      shipper_contracts_differ_from_bol: null,
      brokered_revenue: null,
      bill_of_lading_type: null,
      declared_value_percentage: null,
      bill_of_lading_issued_by: null,
    },
  },
  driver_details: {
    drivers: [],
  },
  previous_claim_details: {
    claims: [
      {
        key: getUniqueId(), driver_no: null, auto_no: null, date_ymd: null,
        coverage_claim_paid_under_bi: null, coverage_claim_paid_under_pd: null,
        coverage_claim_paid_under_ab: null, coverage_claim_paid_under_dcpd: null,
        coverage_claim_paid_under_ua: null, coverage_claim_paid_under_collision: null,
        coverage_claim_paid_under_comp: null, amount_paid_or_estimate: null,
        location_province_city: null, details: null,
      },
    ],
  },
  cargo_claim_details: {
    cargo_claims: [
      {
        key: getUniqueId(), date_of_loss: null, type_of_claim: null,
        deductible: null, payment: null, expenses: null, reserve: null,
      },
    ],
  },
  conviction_details: {
    convictions: [
      {
        key: getUniqueId(), driver_no: null, driver_name: null,
        date_of_conviction: null, conviction_code: null, description: null,
        conviction_as_result_of_accident: null, number_of_kms_over: null,
        suspended: null, suspension_reason: null,
      },
    ],
  },
  driver_hiring_questionnaire: {
    driver_hiring: {
      documented_hiring_policy: null,
      minimum_hiring_requirements: null,
      resumes_applications_retained: null,
      references_checked: null,
      criminal_background_checks: null,
      employment_references_documented: null,
      commercial_experience_verified: null,
      license_verified: null,
      abstracts_review_frequency: null,
      cvor_records_reviewed: null,
      medical_certificates_current: null,
      air_brake_endorsements_verified: null,
      legal_work_status_maintained: null,
      qualification_files_complete: null,
      min_age_hired: null,
      min_experience_requirement: null,
      formal_orientation_program: null,
      orientation_includes: [],
      sign_orientation_documents: null,
    },
  },
  driver_training_questionnaire: {
    driver_training: {
      ongoing_training_conducted: null,
      training_frequency: null,
      training_documented: null,
      trained_on: [],
      new_regulations_communicated: null,
    },
  },
  driver_monitoring_questionnaire: {
    driver_monitoring: {
      abstracts_monitored: null,
      monitoring_frequency: null,
      telematics_used: null,
      speeding_monitored: null,
      harsh_braking_monitored: null,
      seat_belt_monitored: null,
      idling_reports_reviewed: null,
      gps_reports_reviewed: null,
      customer_complaints_tracked: null,
    },
  },
  driver_discipline_questionnaire: {
    driver_discipline: {
      progressive_discipline_policy: null,
      violations_triggering_discipline: null,
      discipline_documented: null,
      coaching_sessions_documented: null,
      repeat_offenders_monitored: null,
      drivers_rewarded_safe_driving: null,
      violations_monitored: [],
    },
  },
  vehicle_management_questionnaire: {
    vehicle_management: {
      fleet_list_maintained: null,
      vehicle_assignments_documented: null,
      vin_records_maintained: null,
      registration_current: null,
      insurance_current: null,
      dashcams_equipped: null,
      telematics_installed: null,
      preventive_maintenance_schedule: null,
      maintenance_software: null,
      service_intervals: null,
      maintenance_records_retained: null,
      repairs_documented: null,
      daily_inspection_reports_completed: null,
      deficiencies_repaired: null,
      defect_reporting_process: null,
      inspection_records_retained: null,
      vehicle_condition_check: [],
    },
  },
  cargo_safety_questionnaire: {
    cargo_safety: {
      cargo_securement_policy: null,
      driver_training: null,
      load_inspections: null,
      weight_compliance: null,
      axle_weights_verified: null,
      load_distribution: null,
      hazardous_goods_procedures: null,
      securement_equipment_inspected: null,
      damaged_straps_removed: null,
      chains_inspected: null,
      tarps_inspected: null,
    },
  },
  cargo_operations_questionnaire: {
    cargo_operations: {
      haul_loads_require_escort: null,
      haul_oversize_overweight: null,
      cargo_value_exceeds_500k: null,
      placards_required: null,
      passengers_allowed: null,
      operate_under_uiia: null,
      vehicles_used_off_road_logging: null,
      residential_home_delivery: null,
      loads_brokered_to_others: null,
      shipper_contracts_differ_from_bol: null,
      brokered_revenue: null,
      bill_of_lading_type: null,
      declared_value_percentage: null,
      bill_of_lading_issued_by: null,
    },
  },
  property_details: {
    premises: {
      building_construction_walls: null,
      roof: null,
      floor: null,
      area_of_building: null,
      heating: null,
      electricity: null,
      occupancies: null,
      max_vehicles_stored_inside_total_value: null,
      documents_required: [],
    },
  },
  document_details: null,
  declaration_details: {
    declaration_text: null,
    applicant_signature: null,
    applicant_date: null,
    applicant_name_title: null,
    broker_signature: null,
    broker_date: null,
    broker_name_title: null,
  },
  status: "ACTIVE",
  created_by_id: null,
  created_by_name: null,
  modified_by_id: null,
  modified_by_name: null,
  create_ts: null,
  insert_ts: null,
};

export const initialRiskProfileV2State: IRiskProfileV2State = {
  list: {
    data: [],
    loading: LoadState.NotLoaded,
    error: null,
  },
  form: {
    data: INITIAL_RISK_PROFILE_V2,
    loading: LoadState.NotLoaded,
    saveLoading: false,
    error: null,
  },
};
